import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SignaturePad } from '../SignaturePad';
import type { SignatureStepProps } from '../types';

export function SignatureStep({
  description,
  recordCount,
  error,
  hasSignature,
  signatureUrl,
  isCreatingSignature,
  allowEdit = true,
  confirmationMessage,
  isConfirmed,
  needsConfirmation,
  onConfirmationChange,
  onSaveSignature,
  onStartCreating,
  onCancelCreating,
}: SignatureStepProps) {
  const { t } = useTranslation('signature');

  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const imageLoadFailed = failedUrl !== null && failedUrl === signatureUrl;

  const getDescriptionText = () => {
    if (description) {
      return description;
    }
    return t('descriptionDefault', { count: recordCount });
  };

  const isSignatureUrlMissing = !signatureUrl;
  const showSignatureError = hasSignature && (isSignatureUrlMissing || imageLoadFailed);

  const renderExistingSignature = () => {
    if (!hasSignature || isCreatingSignature || showSignatureError) {
      return null;
    }

    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {t('yourSignature')}
        </Typography>
        {needsConfirmation && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {t('replicatedSignatureNotice')}
          </Alert>
        )}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            backgroundColor: 'grey.50',
            mb: 2,
          }}
        >
          <img
            src={signatureUrl ?? ''}
            alt={t('yourSignatureAlt')}
            onError={() => setFailedUrl(signatureUrl ?? '')}
            style={{
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: '0 auto',
              maxHeight: 100,
            }}
          />
        </Box>
        {allowEdit && (
          <Button variant="text" size="small" onClick={onStartCreating}>
            {t('editSignature')}
          </Button>
        )}
      </Box>
    );
  };

  const renderSignatureError = () => {
    if (!showSignatureError || isCreatingSignature) {
      return null;
    }

    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={onStartCreating}>
            {t('redrawSignature')}
          </Button>
        }
      >
        {t('signatureLoadFailed')}
      </Alert>
    );
  };

  const renderSignaturePad = () => {
    const shouldRenderPad = !hasSignature || isCreatingSignature || showSignatureError;
    if (!shouldRenderPad) {
      return null;
    }

    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {hasSignature ? t('newSignature') : t('drawSignature')}
        </Typography>
        <SignaturePad height={150} onSave={onSaveSignature} saveButtonText={t('saveSignature')} />
        {hasSignature && isCreatingSignature && (
          <Button variant="text" size="small" onClick={onCancelCreating} sx={{ mt: 1 }}>
            {t('cancelButton')}
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {getDescriptionText()}
      </Typography>
      {confirmationMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!isConfirmed}
                onChange={(e) => onConfirmationChange?.(e.target.checked)}
                color="warning"
              />
            }
            label={confirmationMessage}
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {renderExistingSignature()}
      {renderSignatureError()}
      {renderSignaturePad()}
    </Box>
  );
}
